/**
 * Runtime public config, fetched from /api/config at startup instead of baked
 * in at build time via NEXT_PUBLIC_* vars. Lets the backend API base be
 * repointed by editing app settings in the Azure Portal (effective after a
 * function-host/SWA restart) — no rebuild.
 *
 * NON-SECRET values only — these are already public in the client bundle.
 * Secrets stay server-side in their own token/proxy functions.
 *
 * Moved off NEXT_PUBLIC so far: `roastApiBase` and the two avatars'
 * arcwareShareId / embodimentName / displayName / voiceId / wsEndpoint /
 * wsStreamerId. Other config (object-fit, commander name, …) still reads from
 * NEXT_PUBLIC_*. (Azure Functions are served same-origin under the web app's
 * API, so there is no function base.)
 */

/** The runtime-configurable subset of one avatar's identity/connection. */
export interface AvatarConfig {
  embodimentName: string;
  displayName: string;
  voiceId: string;
  arcwareShareId: string;
  /** Direct Epic Pixel Streaming signalling WebSocket (e.g. Shadow GPU). When
   *  set and no Arcware share id is used, RoastAvatar connects via this. */
  wsEndpoint: string;
  wsStreamerId: string;
}

export interface RuntimeConfig {
  /** Base URL of the humanoid-roast backend (empty = same origin). */
  roastApiBase: string;
  /** Speech-to-text language for the demo (e.g. "de"); empty = auto-detect. */
  sttLanguage: string;
  /** The two on-stage avatars (index 0 = left/Ava, 1 = right/Johannes). */
  avatars: AvatarConfig[];
}

const FALLBACK: RuntimeConfig = { roastApiBase: '', sttLanguage: '', avatars: [] };

function parseAvatar(a: any): AvatarConfig {
  return {
    embodimentName: strip(a?.embodimentName),
    displayName: strip(a?.displayName),
    voiceId: strip(a?.voiceId),
    arcwareShareId: strip(a?.arcwareShareId),
    wsEndpoint: strip(a?.wsEndpoint),
    wsStreamerId: strip(a?.wsStreamerId),
  };
}

let cache: RuntimeConfig | null = null;
let inflight: Promise<RuntimeConfig> | null = null;

const strip = (v: unknown): string => (typeof v === 'string' ? v.replace(/\/$/, '') : '');

/** Fetch /api/config once and memoize. Never rejects — falls back to same-origin. */
export function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = fetch('/api/config')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`config ${r.status}`))))
      .then((c) => {
        cache = {
          roastApiBase: strip(c?.roastApiBase),
          sttLanguage: strip(c?.sttLanguage),
          avatars: Array.isArray(c?.avatars) ? c.avatars.map(parseAvatar) : [],
        };
        return cache;
      })
      .catch((err) => {
        console.error('[runtime-config] load failed; using same-origin fallback:', err);
        cache = FALLBACK; // cache the fallback so we don't refetch in a loop
        return cache;
      });
  }
  return inflight;
}

/** Synchronous read of the cached config (same-origin fallback until loaded). */
export function runtimeConfig(): RuntimeConfig {
  return cache ?? FALLBACK;
}

// Warm the cache as early as possible on the client.
if (typeof window !== 'undefined') {
  void loadRuntimeConfig();
}
